import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Mail, Phone, Waves, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("token");
  const fulfilled = searchParams.get("fulfilled") === "1";
  const [state, setState] = useState(fulfilled ? "success" : orderId ? "checking" : "error");
  const [info, setInfo] = useState(fulfilled ? { lead_id: searchParams.get("pratica") } : null);
  const startedRef = useRef(false);

  useEffect(() => {
    document.title = "Pagamento — ENEA Specialist";
    if (!orderId || fulfilled || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post(`/paypal/capture/${orderId}`);
        if (data.status === "paid") {
          setInfo(data);
          setState("success");
        } else {
          setInfo(data);
          setState("pending");
        }
      } catch (e) {
        setState("error");
      }
    })();
  }, [orderId, fulfilled]);

  return (
    <div className="min-h-screen bg-[#F0F9FC]" data-testid="payment-success-page">
      <header className="sticky top-0 z-40 bg-[#0b2545] text-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#A9D6E5] to-[#14B8A6] flex items-center justify-center">
              <Waves className="w-4 h-4 text-[#0b2545]" />
            </span>
            <span className="text-sm font-semibold tracking-tight leading-tight">
              Home Exchange Copilot AI
              <span className="block text-[10px] font-normal text-[#A9D6E5]">Gruppo Vela</span>
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[#A9D6E5] hover:text-white transition">
            <ArrowLeft className="w-3.5 h-3.5" /> Torna alla home
          </Link>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl shadow-slate-200/60 p-8 text-center">
          {state === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 className="w-12 h-12 text-[#14B8A6] animate-spin mx-auto" />
              <h1 className="mt-4 text-xl font-display font-semibold text-[#0b2545]">Conferma del pagamento PayPal in corso…</h1>
              <p className="mt-2 text-sm text-slate-600">Pochi secondi: stiamo verificando la transazione con PayPal.</p>
            </div>
          )}

          {state === "success" && (
            <div data-testid="payment-success">
              <span className="w-16 h-16 rounded-full bg-[#14B8A6]/10 border border-[#14B8A6]/30 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-9 h-9 text-[#14B8A6]" />
              </span>
              <h1 className="mt-4 text-2xl font-display font-semibold text-[#0b2545]">
                Pagamento confermato{info?.amount_eur ? ` — €${info.amount_eur}` : ""} 💎
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Grazie per la fiducia! La tua <b>Valutazione Premium ENEA Specialist</b> è in preparazione.
              </p>
              <div className="mt-5 text-left space-y-2.5 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700">
                <div className="flex items-start gap-2.5">
                  <Mail className="w-4 h-4 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                  <span><b>Entro pochi minuti</b> riceverai via email la brochure completa di oltre 10 pagine, con il riepilogo dell&apos;ordine e i tuoi dati di fatturazione. <b>Conserva l&apos;email come ricevuta</b>: la fattura sarà emessa dall&apos;agenzia entro 12 giorni.</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <Phone className="w-4 h-4 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                  <span><b>Call di 30 minuti inclusa</b>: prenotala tu subito qui sotto, scegliendo giorno e ora che preferisci.</span>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                <a href="https://calendly.com/velecinquesrl/30min" target="_blank" rel="noreferrer"
                  onClick={() => { api.post("/calendly-click", { lead_id: info?.lead_id || null }).catch(() => {}); }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#14B8A6] text-[#06202b] text-sm font-bold hover:bg-[#2dd4bf] shadow-md"
                  data-testid="payment-calendly-cta">
                  📅 Prenota la call di 30 minuti
                </a>
                {info?.lead_id && (
                  <Link to={`/recensione?pratica=${info.lead_id}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-400"
                    data-testid="payment-review-cta">
                    ⭐ Lascia una recensione
                  </Link>
                )}
                <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c]" data-testid="payment-success-home">
                  Torna alla home
                </Link>
              </div>
            </div>
          )}

          {state === "pending" && (
            <div data-testid="payment-pending">
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto" />
              <h1 className="mt-4 text-xl font-display font-semibold text-[#0b2545]">Pagamento in verifica</h1>
              <p className="mt-2 text-sm text-slate-600">
                {info?.detail || "PayPal non ha ancora confermato il pagamento. Se hai completato il pagamento, ricarica questa pagina tra qualche istante."}
              </p>
              <button type="button" onClick={() => window.location.reload()}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c]"
                data-testid="payment-retry">
                Ricarica la pagina
              </button>
            </div>
          )}

          {state === "error" && (
            <div data-testid="payment-error">
              <XCircle className="w-12 h-12 text-red-400 mx-auto" />
              <h1 className="mt-4 text-xl font-display font-semibold text-[#0b2545]">Pagina non valida</h1>
              <p className="mt-2 text-sm text-slate-600">
                Non troviamo i dati del pagamento. Se hai pagato e non ricevi la brochure entro 15 minuti, scrivici: nessun addebito va perso, verifichiamo noi con PayPal.
              </p>
              <Link to="/valutazione" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c]" data-testid="payment-error-back">
                Torna alla valutazione
              </Link>
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-[11px] text-slate-500">Pagamento gestito in sicurezza da PayPal · Gruppo Vela — Vele Cinque S.r.l.</p>
      </div>
    </div>
  );
}
